import useApiQuery from '@/hooks/useApiQuery';
import useLiveData from '@/hooks/useLiveData';
import useApiDelete from '@/hooks/useApiDelete';
import useTitle from '@/hooks/useTitle';
import {
  Avatar,
  Typography,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
} from '@mui/material';
import { Box } from '@mui/system';
import { useRouteLoaderData } from 'react-router-dom';
import { useState, useMemo, useEffect } from 'react';
import useAppStore from '@/store/app';
import { Delete } from '@mui/icons-material';
import BucketForm from '@/components/forms/bucket/BucketForm';
import ListView from '@/components/datagrid/ListView';
import useSearchStore from '@/store/search';

export default function Buckets() {
  useTitle('Buckets');
  const { users } = useRouteLoaderData('auth');
  const [selectedRows, setSelectedRows] = useState([]);
  const { selectedItem, setSelectedItem } = useAppStore();

  const [type, setType] = useState('ALL');
  const [status, setStatus] = useState('ALL');

  const [searchQuery, setSearchQuery] = useState('');
  const { registerLocalSearch, unregisterLocalSearch } = useSearchStore();

  useEffect(() => {
    registerLocalSearch({
      key: 'buckets',
      label: 'Buckets',
      onSearch: (query) => setSearchQuery(query),
      onClear: () => setSearchQuery(''),
    });

    return () => {
      unregisterLocalSearch();
    };
  }, [registerLocalSearch, unregisterLocalSearch]);

  const mongoFilter = useMemo(() => {
    const filter = { $and: [] };

    if (searchQuery) {
      filter.$and.push({
        $or: [
          { 'items.articleNumber': { $regex: searchQuery, $options: 'i' } },
          { 'items.name': { $regex: searchQuery, $options: 'i' } },
          { 'items.serialNumber': { $regex: searchQuery, $options: 'i' } },
          { contact: { $regex: searchQuery, $options: 'i' } },
        ],
      });
    }

    if (type !== 'ALL') {
      filter.$and.push({ type: type });
    }

    if (status !== 'ALL') {
      filter.$and.push({ status: status });
    }

    if (filter.$and.length === 0) {
      return {};
    }

    return filter;
  }, [searchQuery, type, status]);

  const { mutateAsync: deleteBucket } = useApiDelete('bucket');

  const {
    data: buckets,
    isPending,
    refetch,
  } = useApiQuery('bucket', mongoFilter, true);
  useLiveData('bucket');

  const handleDelete = () => {
    selectedRows.forEach((id) => {
      if (selectedItem === id) {
        setSelectedItem(null);
      }
      deleteBucket(id);
    });
    setSelectedRows([]);
  };

  const columns = [
    {
      field: 'user',
      headerName: 'Benutzer',
      align: 'center',
      flex: 1,
      renderCell: ({ row }) => {
        const currentUser = users.find((user) => user._id === row.user);
        return (
          <Box
            sx={{
              display: 'flex',
              flexFlow: 'row',
              alignItems: 'center',
              height: '100%',
            }}
          >
            <Avatar
              src={
                currentUser?.profilePicture &&
                `${import.meta.env.VITE_FILE_URL}/${currentUser?.profilePicture}`
              }
              sx={{ width: '32px', height: '32px', mr: 1 }}
            />
            <Box
              sx={{
                display: 'flex',
                flexFlow: 'column',
                justifyContent: 'center',
                alignItems: 'flex-start',
              }}
            >
              <Typography variant="body1">
                {`${currentUser?.firstName} ${currentUser?.lastName}`}
              </Typography>
            </Box>
          </Box>
        );
      },
    },
    {
      field: 'contact',
      headerName: 'Kunde',
      flex: 1,
    },
    {
      field: 'orderNumber',
      headerName: 'Auftrags-Nr.',
      flex: 1,
    },
    {
      field: 'type',
      headerName: 'Type',
      flex: 1,
      valueGetter: (value) =>
        `${value === 'RECEIVED' ? 'Annahme' : 'Mitnahme'}`,
    },
    {
      field: 'status',
      headerName: 'Status',
      flex: 1,
      valueGetter: (value) =>
        `${value === 'OPEN' ? 'Offen' : value === 'CLOSED' ? 'Geschlossen' : 'Abgerechnet'}`,
    },
    {
      field: 'createdAt',
      headerName: 'Erstellt',
      flex: 1,
      valueGetter: (value) =>
        new Date(value).toLocaleDateString('de-DE', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
        }),
    },
  ];

  const actions = [
    <FormControl
      key="Typ"
      size="small"
      variant="standard"
      sx={{ minWidth: 100 }}
    >
      <InputLabel id="filterSelectLabelTyp">Typ</InputLabel>
      <Select
        labelId="filterSelectLabelTyp"
        id="filterSelectType"
        value={type}
        label="Typ"
        onChange={(event) => setType(event.target.value)}
      >
        <MenuItem key={'ALL'} value={'ALL'}>
          Alle
        </MenuItem>
        <MenuItem key={'RECEIVED'} value={'RECEIVED'}>
          Annahme
        </MenuItem>
        <MenuItem key={'DELIVERY'} value={'DELIVERY'}>
          Mitnahme
        </MenuItem>
      </Select>
    </FormControl>,
    <FormControl
      key="Status"
      size="small"
      variant="standard"
      sx={{ minWidth: 100 }}
    >
      <InputLabel id="filterSelectLabelTyp">Status</InputLabel>
      <Select
        labelId="filterSelectLabelStatus"
        id="filterSelectStatus"
        value={status}
        label="Status"
        onChange={(event) => setStatus(event.target.value)}
      >
        <MenuItem key={'ALL'} value={'ALL'}>
          Alle
        </MenuItem>
        <MenuItem key={'OPEN'} value={'OPEN'}>
          Offen
        </MenuItem>
        <MenuItem key={'CLOSED'} value={'CLOSED'}>
          Geschlossen
        </MenuItem>
        <MenuItem key={'BILLED'} value={'BILLED'}>
          Abgerechnet
        </MenuItem>
      </Select>
    </FormControl>,
    <Box key={22} sx={{ flex: 1 }} />,
    {
      title: 'Löschen',
      label: 'Löschen',
      color: 'error',
      selection: 'multi',
      icon: Delete,
      onClick: handleDelete,
    },
  ];

  return (
    <ListView
      key={JSON.stringify(mongoFilter)}
      rows={buckets || []}
      columns={columns}
      actions={actions}
      loading={isPending}
      setSelectedRows={setSelectedRows}
      selectedRows={selectedRows}
      detailComponents={[{ Component: BucketForm, title: 'Artikel' }]}
      refetch={refetch}
    />
  );
}
